import React, { useEffect, useId, useRef } from 'react';
import { _t } from '../../utils/i18n.js';
import { trapTabKey } from '../../utils/focusTrap.js';

export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-2xl', size = 'm', fullHeight = false, style = {} }) => {
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const openerRef = useRef(null);
  const titleId = useId();

  // Read while rendering the opening, before an autoFocus child takes the focus at commit.
  if (isOpen && openerRef.current === null) openerRef.current = document.activeElement;

  // Focus enters the dialog on open (unless a child already took it) and returns to the opener on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = modalRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => {
      // StrictMode replays this cleanup while the dialog is still mounted: only a real close restores.
      if (dialog?.isConnected) return;
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      // Ne pas fermer si on clique sur un élément React Select
      if (e.target.closest('[class*="react-select"]') ||
          e.target.closest('.react-select-container') ||
          e.target.closest('[id*="react-select"]')) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(e.target) && isOpen) {
        onClose();
      }
    };

    // Prevent wheel events from propagating to the body
    const handleWheel = (e) => {
      // Only if the modal is open and the event is within the modal
      if (isOpen && modalRef.current?.contains(e.target)) {
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('wheel', handleWheel, { passive: false });

    // Prevent scrolling on body when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Define size classes
  const sizeClasses = {
    'xs': 'max-w-sm max-h-[65vh]',
    's': 'max-w-md max-h-[60vh]',
    'm': 'max-w-2xl max-h-[80vh]',
    'md': 'max-w-3xl max-h-[80vh]',
    'l': 'max-w-[95vw] max-h-[90vh] w-[95vw]',
    'xl': 'max-w-[98vw] max-h-[95vh] w-[98vw]'
  };

  // Use size prop if provided, otherwise use maxWidth
  const sizeClass = size ? sizeClasses[size] : maxWidth;

  // Determine content class based on fullHeight prop.
  //
  // The fullHeight body is a flex column, and `min-h-0` is load-bearing. Both are
  // needed for a child to scroll, measured in a browser:
  //  - a flex item defaults to min-height:auto, so without `min-h-0` this area grows
  //    past the modal instead of being clipped to it;
  //  - `height: 100%` on a child does NOT resolve against a flex item's used height,
  //    so an `h-full overflow-y-auto` child ends up as tall as its content and never
  //    scrolls. The child must be a flex item itself: `flex-1 min-h-0 overflow-y-auto`.
  // A child that manages its own scrolling is unaffected — under a column flex parent
  // it keeps the exact width and height it had as a block child.
  const contentClass = fullHeight
    ? "p-0 flex-1 min-h-0 overflow-hidden flex flex-col"
    : "p-4 overflow-y-auto";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={(event) => trapTabKey(event, modalRef.current)}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl ${sizeClass} w-full overflow-hidden focus:outline-none ${fullHeight ? 'flex flex-col' : ''}`}
        style={style}
      >
        <div className="flex-none flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id={titleId} className="text-lg font-semibold text-gray-800 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={_t('Close')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          ref={contentRef}
          className={contentClass}
          style={{ maxHeight: fullHeight ? 'none' : 'calc(100% - 4rem)' }}
          onWheel={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  );
};